import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import { Trash2, Settings, X, Clock, Repeat, FileJson, ClipboardCopy, Hash, Globe, Calendar, Activity } from "lucide-react";
import NodeDocsButton from "../../docs/NodeDocsButton";

// ──────────────────────────────────────────────────────────────────────────────
// Interval unit model (n8n-style)
// ──────────────────────────────────────────────────────────────────────────────

type IntervalUnit =
  | "seconds"
  | "minutes"
  | "hours"
  | "days"
  | "weeks"
  | "months"
  | "cron";

// Minimum + default value for each unit. Seconds is floored at 30 to
// match n8n — under that the scheduler service gets hammered and most
// downstream workflows can't even complete inside the window.
const UNIT_DEFAULTS: Record<IntervalUnit, { defaultValue: number; min: number; max?: number; label: string }> = {
  seconds: { defaultValue: 30, min: 30, max: 59,   label: "Seconds" },
  minutes: { defaultValue: 5,  min: 1,  max: 59,   label: "Minutes" },
  hours:   { defaultValue: 1,  min: 1,  max: 23,   label: "Hours"   },
  days:    { defaultValue: 1,  min: 1,  max: 31,   label: "Days"    },
  weeks:   { defaultValue: 1,  min: 1,  max: 52,   label: "Weeks"   },
  months:  { defaultValue: 1,  min: 1,  max: 12,   label: "Months"  },
  cron:    { defaultValue: 0,  min: 0,             label: "Custom (cron)" },
};

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday"    },
  { value: 1, label: "Monday"    },
  { value: 2, label: "Tuesday"   },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday"  },
  { value: 5, label: "Friday"    },
  { value: 6, label: "Saturday"  },
];

const COMMON_TIMEZONES = [
  "Asia/Kolkata",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

interface IntervalState {
  unit: IntervalUnit;
  value: number;        // "every N <unit>"
  atTime: string;       // "HH:MM" — used by days/weeks/months
  dayOfWeek: number;    // 0–6 — used by weeks
  dayOfMonth: number;   // 1–31 — used by months
  cronExpression: string; // used when unit === "cron"
}

// Converts the structured interval state into a cron expression. Returns
// 5-field cron for everything except sub-minute intervals (which require
// 6-field — Agenda's cron-parser supports both).
function buildCronExpression(state: IntervalState): string {
  const { unit, value, atTime, dayOfWeek, dayOfMonth, cronExpression } = state;
  const [hh, mm] = (atTime || "09:00").split(":");
  const n = Math.max(1, Number(value) || 1);

  switch (unit) {
    case "seconds": return `*/${Math.max(30, n)} * * * * *`;
    case "minutes": return `*/${n} * * * *`;
    case "hours":   return `0 */${n} * * *`;
    case "days":    return `${mm} ${hh} */${n} * *`;
    case "weeks":   return `${mm} ${hh} * * ${dayOfWeek}`;       // ignores value (always "every Nth week of year" — most users want "every <day>")
    case "months":  return `${mm} ${hh} ${dayOfMonth} */${n} *`;
    case "cron":    return cronExpression || "";
    default:        return "";
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Display formatters — used by the output panel to render real run data
// ──────────────────────────────────────────────────────────────────────────────

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year:   "numeric",
      month:  "short",
      day:    "numeric",
      hour:   "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Date.now() - t;
    const abs  = Math.abs(diff);
    const fut  = diff < 0;
    const sec  = Math.floor(abs / 1000);
    if (sec < 60)         return fut ? `in ${sec}s`           : `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60)         return fut ? `in ${min}m`           : `${min}m ago`;
    const hr  = Math.floor(min / 60);
    if (hr  < 24)         return fut ? `in ${hr}h ${min % 60}m` : `${hr}h ${min % 60}m ago`;
    const day = Math.floor(hr / 24);
    return fut ? `in ${day}d` : `${day}d ago`;
  } catch {
    return "";
  }
}

function buildFriendlyLabel(state: IntervalState): string {
  const { unit, value, atTime, dayOfWeek, dayOfMonth, cronExpression } = state;
  const n = Math.max(1, Number(value) || 1);
  const time = atTime || "09:00";

  switch (unit) {
    case "seconds": return n === 1 ? "Every second" : `Every ${Math.max(30, n)} seconds`;
    case "minutes": return n === 1 ? "Every minute" : `Every ${n} minutes`;
    case "hours":   return n === 1 ? "Every hour"   : `Every ${n} hours`;
    case "days":    return n === 1 ? `Every day at ${time}` : `Every ${n} days at ${time}`;
    case "weeks":   return `Every ${DAYS_OF_WEEK[dayOfWeek]?.label || "Monday"} at ${time}`;
    case "months":  return n === 1
      ? `On day ${dayOfMonth} of every month at ${time}`
      : `On day ${dayOfMonth} every ${n} months at ${time}`;
    case "cron":    return cronExpression || "Not configured";
    default:        return "Not configured";
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────────

const ScheduleNode: React.FC<NodeProps<Node<any>>> = ({ id, data, selected }) => {
  const { setNodes, deleteElements } = useReactFlow();

  // Interval state — initialised from saved node data or sensible defaults
  const [interval, setIntervalState] = useState<IntervalState>(() => ({
    unit:           (data?.interval_unit as IntervalUnit) || "days",
    value:          typeof data?.interval_value === "number" ? data.interval_value : UNIT_DEFAULTS["days"].defaultValue,
    atTime:         data?.at_time         || "09:00",
    dayOfWeek:      typeof data?.day_of_week  === "number" ? data.day_of_week  : 1,
    dayOfMonth:     typeof data?.day_of_month === "number" ? data.day_of_month : 1,
    cronExpression: data?.cron_expression || "0 9 * * *",
  }));

  const [timezone, setTimezone] = useState<string>(data?.timezone || "Asia/Kolkata");
  const [startAt, setStartAt] = useState<string>(data?.start_at || "");
  const [endAt, setEndAt]     = useState<string>(data?.end_at   || "");
  const [maxRuns, setMaxRuns] = useState<string>(
    data?.max_runs != null ? String(data.max_runs) : "",
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  // Output panel — friendly view by default, raw JSON on toggle
  const [showRawOutput, setShowRawOutput] = useState(false);

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  // Derived
  const effectiveCron   = useMemo(() => buildCronExpression(interval), [interval]);
  const friendlyLabel   = useMemo(() => buildFriendlyLabel(interval),   [interval]);

  // When the user switches units, reset value + sub-fields to that unit's default
  const onUnitChange = useCallback((newUnit: IntervalUnit) => {
    setIntervalState((prev) => ({
      ...prev,
      unit: newUnit,
      value: UNIT_DEFAULTS[newUnit].defaultValue,
    }));
  }, []);

  const onValueChange = useCallback((raw: string) => {
    const num = Number(raw);
    if (!Number.isFinite(num)) return;
    const cfg = UNIT_DEFAULTS[interval.unit];
    const clamped = Math.max(cfg.min, cfg.max ? Math.min(cfg.max, num) : num);
    setIntervalState((prev) => ({ ...prev, value: clamped }));
  }, [interval.unit]);

  // Sync state back into the node
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              name: "ScheduleNode",
              data: {
                ...node.data,
                name: "ScheduleNode",

                // The structured fields the UI cares about
                interval_unit:  interval.unit,
                interval_value: interval.value,
                at_time:        interval.atTime,
                day_of_week:    interval.dayOfWeek,
                day_of_month:   interval.dayOfMonth,

                // The flat cron the backend reads — always kept in sync
                schedule_kind:    interval.unit === "cron" ? "cron" : "interval",
                cron_expression:  effectiveCron,

                // Other shared fields
                timezone,
                start_at: startAt || undefined,
                end_at:   endAt   || undefined,
                max_runs: maxRuns ? Number(maxRuns) : undefined,
              },
            }
          : node,
      ),
    );
  }, [
    id,
    setNodes,
    interval,
    effectiveCron,
    timezone,
    startAt,
    endAt,
    maxRuns,
  ]);

  const isConfigured = effectiveCron.trim().length > 0;
  const statusConfig = isConfigured
    ? { bg: "bg-purple-500", text: "Configured" }
    : { bg: "bg-gray-400",  text: "Not Configured" };

  // Whether the current unit shows the "at this time" field
  const showAtTime     = ["days", "weeks", "months"].includes(interval.unit);
  const showDayOfWeek  = interval.unit === "weeks";
  const showDayOfMonth = interval.unit === "months";

  // Custom cron input replaces the value spinner
  const showValueInput = interval.unit !== "cron";
  const showCronInput  = interval.unit === "cron";

  const unitCfg = UNIT_DEFAULTS[interval.unit];

  return (
    <div
      className={`relative group bg-purple-50 border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        selected ? "border-purple-500 shadow-md" : "border-purple-300 hover:border-purple-400"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg shadow-sm bg-purple-100">
            <Clock className="w-4 h-4 text-purple-700" />
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">Schedule</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg}`} />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType="SCHEDULE" />
          <button
            onClick={handleDeleteNode}
            className="text-red-500 hover:text-gray-700 p-1 rounded transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div
        className="p-2.5 bg-white/80 border border-purple-200 rounded-lg text-xs shadow-inner space-y-1.5"
        onClick={stopPropagation}
      >
        <div className="flex items-center gap-1.5">
          <Repeat className="w-3 h-3 text-purple-600 flex-shrink-0" />
          <span className="text-gray-700 truncate">{friendlyLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
            {timezone}
          </span>
          <span className="text-gray-500 truncate font-mono text-[10px]">
            {effectiveCron}
          </span>
        </div>
      </div>

      <div className="mt-3" onClick={stopPropagation}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsModalOpen(true);
          }}
          className="w-full py-2.5 rounded-lg bg-purple-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-purple-700 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" /> Configure Schedule
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-purple-500 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(147, 51, 234, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />

      {/* Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 rounded">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure Schedule
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Decide how often this workflow should fire automatically.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal body — two columns: config (left) + outputs (right) */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">

                {/* ── Left column — configuration ───────────────────────── */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">

                {/* Trigger interval (unit + value) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger Interval
                  </label>
                  <div className="grid grid-cols-12 gap-2">
                    {/* Value */}
                    {showValueInput && (
                      <div className="col-span-4">
                        <div className="text-[11px] text-gray-500 mb-1">Every</div>
                        <input
                          type="number"
                          min={unitCfg.min}
                          max={unitCfg.max}
                          value={interval.value}
                          onChange={(e) => onValueChange(e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    )}

                    {/* Unit dropdown */}
                    <div className={showValueInput ? "col-span-8" : "col-span-12"}>
                      <div className="text-[11px] text-gray-500 mb-1">Unit</div>
                      <select
                        value={interval.unit}
                        onChange={(e) => onUnitChange(e.target.value as IntervalUnit)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="seconds">Seconds</option>
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                        <option value="cron">Custom (cron expression)</option>
                      </select>
                    </div>
                  </div>

                  {/* Helper line under interval picker */}
                  {interval.unit === "seconds" && (
                    <p className="text-[11px] text-amber-600 mt-1.5">
                      Minimum 30 seconds. Sub-minute schedules are CPU-intensive — use sparingly.
                    </p>
                  )}
                  {interval.unit !== "cron" && (
                    <p className="text-[11px] text-gray-500 mt-1.5">
                      Resolves to cron <span className="font-mono">{effectiveCron}</span>
                    </p>
                  )}
                </div>

                {/* Day of week (weeks only) */}
                {showDayOfWeek && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day of week
                    </label>
                    <select
                      value={interval.dayOfWeek}
                      onChange={(e) =>
                        setIntervalState((prev) => ({ ...prev, dayOfWeek: Number(e.target.value) }))
                      }
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {DAYS_OF_WEEK.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Day of month (months only) */}
                {showDayOfMonth && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day of month
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={interval.dayOfMonth}
                      onChange={(e) =>
                        setIntervalState((prev) => ({
                          ...prev,
                          dayOfMonth: Math.max(1, Math.min(31, Number(e.target.value) || 1)),
                        }))
                      }
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      For months with fewer days (e.g. February 30), the trigger is skipped that month.
                    </p>
                  </div>
                )}

                {/* At time (days/weeks/months) */}
                {showAtTime && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      At time
                    </label>
                    <input
                      type="time"
                      value={interval.atTime}
                      onChange={(e) =>
                        setIntervalState((prev) => ({ ...prev, atTime: e.target.value }))
                      }
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}

                {/* Custom cron */}
                {showCronInput && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cron expression
                    </label>
                    <input
                      type="text"
                      value={interval.cronExpression}
                      onChange={(e) =>
                        setIntervalState((prev) => ({ ...prev, cronExpression: e.target.value }))
                      }
                      placeholder="0 9 * * *"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                      Five fields: <span className="font-mono">minute hour day-of-month month day-of-week</span>.
                      For sub-minute, use six fields with seconds first (e.g. <span className="font-mono">*/30 * * * * *</span>).
                    </p>
                  </div>
                )}

                {/* Timezone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>

                {/* Optional range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start at <span className="text-xs text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={startAt}
                      onChange={(e) => setStartAt(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Don't fire before this</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End at <span className="text-xs text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={endAt}
                      onChange={(e) => setEndAt(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Stop firing after this</p>
                  </div>
                </div>

                {/* Max runs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum runs <span className="text-xs text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={maxRuns}
                    onChange={(e) => setMaxRuns(e.target.value)}
                    placeholder="No limit"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Schedule auto-disables itself after this many runs. Blank = no cap.
                  </p>
                </div>

                </div>
                {/* ── End left column ────────────────────────────────────── */}

                {/* ── Right column — node outputs ────────────────────────── */}
                <div className="w-[380px] bg-gray-50 overflow-y-auto p-4 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        data?.outputs ? "bg-green-500" : "bg-gray-400"
                      }`} />
                      Last Run Output
                    </h3>
                    {data?.outputs && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setShowRawOutput((v) => !v)}
                          className="px-2 py-1 text-[10px] font-medium bg-white border border-gray-200 text-gray-600 hover:text-purple-600 hover:border-purple-200 rounded transition-all"
                          title={showRawOutput ? "Show formatted view" : "Show raw JSON"}
                        >
                          {showRawOutput ? "Friendly" : "JSON"}
                        </button>
                        <button
                          onClick={() => navigator.clipboard.writeText(JSON.stringify(data.outputs, null, 2))}
                          className="p-1.5 bg-white border border-gray-200 text-gray-500 hover:text-purple-600 hover:border-purple-200 rounded transition-all"
                          title="Copy JSON"
                        >
                          <ClipboardCopy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col">
                    {data?.outputs ? (
                      showRawOutput ? (
                        // Power-user view — raw JSON
                        <div className="p-3 overflow-auto custom-scrollbar h-full">
                          <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap break-all">
                            {JSON.stringify(data.outputs, null, 2)}
                          </pre>
                        </div>
                      ) : (
                        // Friendly default — labeled cards per field
                        <div className="p-3 overflow-auto custom-scrollbar h-full space-y-2">
                          {/* triggered_at */}
                          <div className="border border-gray-100 rounded-lg p-2.5 bg-gradient-to-br from-emerald-50 to-white">
                            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">
                              <Activity className="w-3 h-3" />
                              Fired at
                            </div>
                            <p className="text-xs text-gray-900 font-medium mt-1">
                              {formatDateTime(data.outputs.triggered_at)}
                            </p>
                            {formatRelative(data.outputs.triggered_at) && (
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                {formatRelative(data.outputs.triggered_at)}
                              </p>
                            )}
                          </div>

                          {/* scheduled_for — only show if it differs from triggered_at */}
                          {data.outputs.scheduled_for &&
                           data.outputs.scheduled_for !== data.outputs.triggered_at && (
                            <div className="border border-gray-100 rounded-lg p-2.5">
                              <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                                <Calendar className="w-3 h-3" />
                                Was scheduled for
                              </div>
                              <p className="text-xs text-gray-900 mt-1">
                                {formatDateTime(data.outputs.scheduled_for)}
                              </p>
                              {formatRelative(data.outputs.scheduled_for) && (
                                <p className="text-[10px] text-gray-500 mt-0.5">
                                  {formatRelative(data.outputs.scheduled_for)}
                                </p>
                              )}
                            </div>
                          )}

                          {/* run_number */}
                          {data.outputs.run_number != null && (
                            <div className="border border-gray-100 rounded-lg p-2.5">
                              <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                                <Hash className="w-3 h-3" />
                                Run number
                              </div>
                              <p className="text-sm text-gray-900 font-semibold mt-1">
                                #{data.outputs.run_number}
                              </p>
                            </div>
                          )}

                          {/* cron_expression + timezone (combined card) */}
                          {(data.outputs.cron_expression || data.outputs.timezone) && (
                            <div className="border border-gray-100 rounded-lg p-2.5">
                              <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                                <Repeat className="w-3 h-3" />
                                Cron
                              </div>
                              {data.outputs.cron_expression && (
                                <p className="text-xs text-gray-900 font-mono mt-1 break-all">
                                  {data.outputs.cron_expression}
                                </p>
                              )}
                              {data.outputs.timezone && (
                                <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                                  <Globe className="w-2.5 h-2.5" />
                                  {data.outputs.timezone}
                                </p>
                              )}
                            </div>
                          )}

                          {/* IDs — small text, less prominent */}
                          {(data.outputs.template_id || data.outputs.schedule_node_id) && (
                            <div className="border border-gray-100 rounded-lg p-2.5 bg-gray-50/70 space-y-1">
                              {data.outputs.template_id && (
                                <div>
                                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Template ID</span>
                                  <p className="text-[10px] text-gray-700 font-mono break-all">{data.outputs.template_id}</p>
                                </div>
                              )}
                              {data.outputs.schedule_node_id && (
                                <div>
                                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Schedule node ID</span>
                                  <p className="text-[10px] text-gray-700 font-mono break-all">{data.outputs.schedule_node_id}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Any extra fields the handler returned but we don't have a card for */}
                          {(() => {
                            const known = new Set([
                              "triggered_at", "scheduled_for", "run_number",
                              "template_id", "schedule_node_id",
                              "cron_expression", "timezone",
                            ]);
                            const extras = Object.entries(data.outputs).filter(([k]) => !known.has(k));
                            if (extras.length === 0) return null;
                            return (
                              <div className="border border-amber-100 bg-amber-50/50 rounded-lg p-2.5">
                                <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1">
                                  Extra fields
                                </div>
                                <pre className="text-[11px] font-mono text-gray-700 whitespace-pre-wrap break-all">
                                  {JSON.stringify(Object.fromEntries(extras), null, 2)}
                                </pre>
                              </div>
                            );
                          })()}
                        </div>
                      )
                    ) : (
                      // Schema preview — what this node WILL output on every tick
                      <div className="flex flex-col p-3 gap-3">
                        <div className="flex items-start gap-2 text-xs text-gray-600 bg-purple-50 border border-purple-100 rounded p-2.5">
                          <FileJson className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                          <p>
                            This node hasn't fired yet. Below is the shape of the
                            payload it will emit on every cron tick. Downstream
                            nodes can reference these via{" "}
                            <span className="font-mono">{"{{ScheduleNode.outputs.<field>}}"}</span>.
                          </p>
                        </div>

                        <div className="space-y-1.5">
                          {[
                            { key: "triggered_at",     type: "string (ISO)",  desc: "When this tick actually fired" },
                            { key: "scheduled_for",    type: "string (ISO)",  desc: "When this tick was supposed to fire" },
                            { key: "run_number",       type: "number",        desc: "1, 2, 3, … (lifetime counter)" },
                            { key: "template_id",     type: "string",        desc: "ID of this workflow template" },
                            { key: "schedule_node_id", type: "string",        desc: "ID of this Schedule node" },
                            { key: "cron_expression",  type: "string",        desc: "Resolved cron (e.g. `*/5 * * * *`)" },
                            { key: "timezone",         type: "string",        desc: "IANA timezone the cron evaluated in" },
                          ].map((f) => (
                            <div key={f.key} className="text-xs border border-gray-100 rounded p-2 bg-gray-50">
                              <div className="flex items-baseline gap-2">
                                <code className="font-mono text-purple-700">{f.key}</code>
                                <span className="text-[10px] text-gray-500">{f.type}</span>
                              </div>
                              <p className="text-[11px] text-gray-600 mt-0.5">{f.desc}</p>
                            </div>
                          ))}
                        </div>

                        <div className="text-[11px] text-gray-500 mt-1 italic">
                          Save the workflow and wait for the first cron tick — the
                          live JSON will replace this preview.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* ── End right column ───────────────────────────────────── */}

              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default React.memo(ScheduleNode);
export { ScheduleNode };
